import Component from "@glimmer/component";
import { on } from "@ember/modifier";
import { trackedArray } from "@ember/reactive/collections";

export interface EventLoggerOptions {
  maxLogs?: number;
  mirrorToConsole?: boolean;
}

function formatArg(arg: unknown): string {
  if (typeof arg === "string") return arg;
  if (
    typeof arg === "number" ||
    typeof arg === "boolean" ||
    typeof arg === "bigint" ||
    typeof arg === "symbol"
  ) {
    return String(arg);
  }
  if (arg === null) return "null";
  if (arg === undefined) return "undefined";
  if (typeof arg === "object") {
    if ("name" in arg && typeof (arg as { name?: unknown }).name === "string") {
      return (arg as { name: string }).name;
    }
    if ("id" in arg && typeof (arg as { id?: unknown }).id === "string") {
      return (arg as { id: string }).id;
    }
    try {
      return JSON.stringify(arg);
    } catch {
      return "[Object]";
    }
  }
  return "[unknown]";
}

export class EventLogger {
  logs: string[] = trackedArray<string>();
  maxLogs: number;
  mirrorToConsole: boolean;

  constructor(options: EventLoggerOptions = {}) {
    this.maxLogs = options.maxLogs ?? 20;
    this.mirrorToConsole = options.mirrorToConsole ?? true;
  }

  log = (msg: string, ...args: unknown[]) => {
    const time = new Date().toLocaleTimeString();
    let formattedMsg = `[${time}] ${msg}`;
    if (args.length > 0) {
      const formattedArgs = args.map(formatArg).join(", ");
      formattedMsg = `${formattedMsg} ${formattedArgs}`;
    }

    if (this.mirrorToConsole) {
      // The console gets the raw args
      console.log(`[sandbox] ${msg}`, ...args);
    }

    this.logs.unshift(formattedMsg);
    if (this.logs.length > this.maxLogs) {
      this.logs.pop();
    }
  };

  clear = () => {
    this.logs.length = 0;
  };
}

export interface EventLogSignature {
  Element: HTMLElement;
  Args: {
    logger: EventLogger;
    title?: string;
    emptyMessage?: string;
  };
}

export default class EventLog extends Component<EventLogSignature> {
  get title(): string {
    return this.args.title ?? "Event Log";
  }

  get emptyMessage(): string {
    return this.args.emptyMessage ?? "No events logged yet.";
  }

  <template>
    <div class="space-y-1">
      <div class="flex items-center justify-between">
        <h2 class="text-sm font-semibold uppercase tracking-wider text-amber-400">
          {{this.title}}
        </h2>
        <button
          type="button"
          class="px-2 py-0.5 text-xs rounded text-slate-400 hover:text-slate-200 underline cursor-pointer"
          {{on "click" @logger.clear}}
        >
          Clear Log
        </button>
      </div>

      <div
        class="border border-slate-800 rounded-lg bg-slate-950/80 p-3 overflow-y-auto font-mono text-xs space-y-1"
        ...attributes
      >
        {{#if @logger.logs.length}}
          {{#each @logger.logs as |msg|}}
            <div class="text-amber-200/90">{{msg}}</div>
          {{/each}}
        {{else}}
          <span class="text-slate-600 italic">{{this.emptyMessage}}</span>
        {{/if}}
      </div>
    </div>
  </template>
}
